import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { filter, Subject, tap } from 'rxjs';
import { ApplicationTypeLabelMapping } from '../../../../core/constants/application-type-enum';
import { nameOf } from '../../../../core/functions/string-helpers';
import { FilterOption } from '../../../../core/models/grid/filter-option';
import { IGridComponent } from '../../../../core/models/grid/grid-component';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { CurrencyFormattingService } from '../../../../core/services/currency-formatting/currency-formatting.service';
import { GridFilterDataService } from '../../../../core/services/grid-filter-data/grid-filter-data.service';
import { OrderIdCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/order-fulfillment/order-id-cell-renderer/order-id-cell-renderer.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { MultiSelectFilterComponent } from '../../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { ApplicationPurposeCodeLabelMapping } from '../../shared/constants/application-purpose-codes';
import { OrderService } from '../shared/services/order/order.service';
import { CustomerOrderDashboardGridData } from './models/customer-order-dashboard-grid-data';
import { CustomerOrderDashboardGridResponse } from './models/customer-order-dashboard-grid-response';
import { formatToDate } from '../../../../core/functions/date-helpers';
import { RouteConstants } from '../../../../core/constants/routes';

@Component({
    selector: 'app-customer-order-dashboard',
    templateUrl: './customer-order-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular, GridToolbarComponent],
})
export class CustomerOrderDashboardComponent
    implements IGridComponent, OnDestroy
{
    private destroy$ = new Subject<void>();

    private readonly orderService = inject(OrderService);
    private readonly userStateService = inject(UserStateService);
    private readonly currencyFormattingService = inject(
        CurrencyFormattingService,
    );
    private readonly filterDataService = inject(GridFilterDataService);

    constructor() {}

    context: unknown;
    rowData: CustomerOrderDashboardGridData[] = [];
    gridApi: GridApi | undefined;
    columnDefs: ColDef[] = [];

    orderStatusFilterOptions: FilterOption[] =
        this.filterDataService.getOrderStatusFilterOptions();
    applicationPurposeFilterOptions: FilterOption[] = [];

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsvOptions: {
            fileName: () => GridHelper.getExportFileName('Orders'),
        },
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
    };

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;

        this.loadDropdownData();
        this.loadGridData();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadGridData() {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        this.orderService
            .getAllForCustomer(authenticatedUser.id)
            .pipe(
                tap((orders) => {
                    this.rowData = this.transformRowData(orders);
                    this.columnDefs = this.getColumnDefs();
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    private loadDropdownData() {
        this.filterDataService
            .getApplicationPurposeFilterOptions()
            .pipe(
                tap((applicationPurposeFilterOptions) => {
                    this.applicationPurposeFilterOptions =
                        applicationPurposeFilterOptions;
                }),
            )
            .subscribe();
    }

    private transformRowData(
        orders: CustomerOrderDashboardGridResponse[],
    ): CustomerOrderDashboardGridData[] {
        return orders.map((order) => ({
            ...order,
            createdAt: formatToDate(order.createdAt),
            totalPrice: this.currencyFormattingService.formatToUSD(
                order.totalPrice,
            ),
            balance: this.currencyFormattingService.formatToUSD(order.balance),
            transmissionLocation: [order.locationCity, order.locationState]
                .filter(Boolean)
                .join(', '),
            customerName: [order.customerFirstName, order.customerLastName]
                .filter(Boolean)
                .join(' '),
        }));
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                field: nameOf<CustomerOrderDashboardGridData>('orderId'),
                headerName: 'Order ID',
                cellRenderer: OrderIdCellRendererComponent,
                cellRendererParams: {
                    returnRoute: RouteConstants.Order.getFullRoute('INDEX'),
                },
                minWidth: 120,
                maxWidth: 140,
            },
            {
                field: nameOf<CustomerOrderDashboardGridData>('createdAt'),
                headerName: 'Created',
                ...GridHelper.defaultDateTimeRendererColDef,
                minWidth: 170,
                maxWidth: 200,
                sort: 'desc',
            },
            {
                headerName: 'PO #',
                minWidth: 110,
                maxWidth: 140,
            },
            {
                field: nameOf<CustomerOrderDashboardGridData>('totalPrice'),
                headerName: 'Total',
                minWidth: 120,
                maxWidth: 150,
                filter: false,
            },
            {
                field: nameOf<CustomerOrderDashboardGridData>('balance'),
                headerName: 'Balance',
                minWidth: 120,
                maxWidth: 150,
                filter: false,
            },
            {
                headerName: 'Services',
                valueFormatter: (gridParams) =>
                    this.formatServicesColumn(
                        gridParams.data as CustomerOrderDashboardGridData,
                    ),
                valueGetter: (gridParams) => gridParams.data.applicationPurpose,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.applicationPurposeFilterOptions.map(
                        (option) => ({
                            label: option.label,
                            value: option.value,
                            selected: option.selected,
                        }),
                    ),
                },
                minWidth: 200,
                flex: 1,
            },
            {
                field: nameOf<CustomerOrderDashboardGridData>(
                    'licenseeEntityName',
                ),
                headerName: 'Applicant',
                minWidth: 180,
                flex: 1,
            },
            {
                field: nameOf<CustomerOrderDashboardGridData>(
                    'transmissionLocation',
                ),
                headerName: 'TX Location',
                minWidth: 150,
                flex: 1,
            },
            {
                field: nameOf<CustomerOrderDashboardGridData>('customerName'),
                headerName: 'User',
                minWidth: 150,
                flex: 1,
            },
        ];
    }

    private formatServicesColumn(
        rowData: CustomerOrderDashboardGridData,
    ): string {
        let formattedServices = `${ApplicationPurposeCodeLabelMapping[rowData.applicationPurpose as keyof typeof ApplicationPurposeCodeLabelMapping]}`;

        if (rowData.applicationType) {
            formattedServices += ` (${ApplicationTypeLabelMapping[rowData.applicationType]})`;
        }

        return formattedServices;
    }
}
